// Browser-storage self-healing for the Firestore offline cache (2026-10-29).
//
// Incident: an admin's Chrome (set to "delete site data when all windows close", which gives
// sites a ~300 MB quota) reached 319 MB of IndexedDB for this origin while the real cached data
// was only ~9 MB. The rest was stale copies left behind by large documents being rewritten in
// full (warriors/notifications, rosterAuditLog, log); Chrome reclaims those lazily. At the quota,
// every Firestore write failed with code "unavailable" and the auth session couldn't be persisted,
// so refreshes logged the user out. The 50 MB cacheSizeBytes bound in firebase.js can't help:
// it only evicts documents no active listener references, and the app listens to nearly all of
// its data.
//
// Two defenses:
//   1. checkStorageOnStartup() runs in main.jsx BEFORE the first render (Firestore is initialized
//      at import time but not started until first use). If this origin's usage is over
//      RESET_THRESHOLD_BYTES, it clears Firestore's IndexedDB cache and shows a brief
//      "Refreshing local data…" notice; the app then re-downloads current data (~9 MB today).
//      It never clears while writes are still queued for upload, and the auth session
//      (a separate IndexedDB database) is left alone, so the user stays signed in.
//   2. isStorageFullError() / requestStorageResetAndReload() back the write-failure banner in
//      App.jsx: when a save fails because storage is full, the banner says so and offers a
//      reset, which sets a flag and reloads so defense 1 clears the cache unconditionally.
import { clearIndexedDbPersistence } from 'firebase/firestore';
import { db } from '../firebase.js';

const RESET_THRESHOLD_BYTES = 100 * 1024 * 1024;
const QUOTA_FRACTION = 0.8;
const COOLDOWN_MS = 6 * 60 * 60 * 1000; // Chrome may report the old usage for a while after a clear
const FORCE_KEY = 'wa_forceStorageReset';
const LAST_RESET_KEY = 'wa_lastStorageReset';

const lsGet = k => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };
const lsDel = k => { try { localStorage.removeItem(k); } catch { /* ignore */ } };

async function estimate() {
  try {
    if (!navigator.storage?.estimate) return null;
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return { usage, quota };
  } catch { return null; }
}

// Resolves true if Firestore's IndexedDB has writes still waiting to upload, or if we can't tell
// (in which case we don't clear). Resolves false if there's no Firestore database at all.
async function hasPendingWrites() {
  try {
    if (!indexedDB.databases) return true;
    const name = (await indexedDB.databases()).map(d => d.name).find(n => n && n.startsWith('firestore/'));
    if (!name) return false;
    return await new Promise(resolve => {
      const req = indexedDB.open(name);
      req.onerror = () => resolve(true);
      req.onblocked = () => resolve(true);
      req.onsuccess = () => {
        const idb = req.result;
        try {
          if (!idb.objectStoreNames.contains('mutations')) { idb.close(); resolve(false); return; }
          const c = idb.transaction('mutations', 'readonly').objectStore('mutations').count();
          c.onsuccess = () => { idb.close(); resolve(c.result > 0); };
          c.onerror = () => { idb.close(); resolve(true); };
        } catch { idb.close(); resolve(true); }
      };
    });
  } catch { return true; }
}

function showRefreshNotice() {
  try {
    const el = document.createElement('div');
    el.textContent = 'Refreshing local data…';
    el.setAttribute('role', 'status');
    el.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:10000;' +
      'background:#0f2a1d;color:#fff;border:1px solid #c9a227;border-radius:4px;padding:10px 16px;' +
      'font:600 12px/1.3 Inter,system-ui,sans-serif;letter-spacing:0.5px;box-shadow:0 8px 24px #00000077;' +
      'transition:opacity .4s;opacity:1;';
    const mount = () => document.body.appendChild(el);
    document.body ? mount() : window.addEventListener('DOMContentLoaded', mount, { once: true });
    setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 450); }, 3000);
  } catch { /* cosmetic only */ }
}

export async function checkStorageOnStartup() {
  try {
    const forced = lsGet(FORCE_KEY) === '1';
    if (!forced) {
      const last = Number(lsGet(LAST_RESET_KEY) || 0);
      if (Date.now() - last < COOLDOWN_MS) return;
      const est = await estimate();
      // Trigger on either an absolute size or being near this origin's quota: a device with a small
      // quota (e.g. ~150 MB) can fill up well before RESET_THRESHOLD_BYTES is reached.
      const nearQuota = !!(est && est.quota && est.usage / est.quota > QUOTA_FRACTION);
      if (!est || (est.usage < RESET_THRESHOLD_BYTES && !nearQuota)) return;
      if (await hasPendingWrites()) {
        console.warn(`[storageHealth] usage ${(est.usage / 1e6).toFixed(0)} MB is over the reset threshold, but writes are still queued for upload — not clearing.`);
        return;
      }
      console.warn(`[storageHealth] usage ${(est.usage / 1e6).toFixed(0)} MB of ${(est.quota / 1e6).toFixed(0)} MB — clearing the Firestore cache.`);
    } else {
      console.warn('[storageHealth] reset requested from the storage-full banner — clearing the Firestore cache.');
    }
    lsDel(FORCE_KEY);
    showRefreshNotice();
    await clearIndexedDbPersistence(db); // rejects if another open tab is using the cache
    lsSet(LAST_RESET_KEY, String(Date.now()));
  } catch (e) {
    // failed-precondition = another tab holds the cache; harmless, try again next load.
    console.warn('[storageHealth] cache clear skipped:', e?.code || e?.message || e);
  }
}

// True when a failed Firestore write was caused by this origin's storage being (nearly) full.
// Firestore reports IndexedDB failures as code "unavailable", the same code as a dropped
// connection, so we confirm with the storage estimate rather than trusting the code alone.
export async function isStorageFullError(e) {
  const msg = String(e?.message || '') + ' ' + String(e?.name || '');
  if (/QuotaExceeded|quota/i.test(msg)) return true;
  if (e?.code !== 'unavailable' && !/IndexedDb/i.test(msg)) return false;
  const est = await estimate();
  return !!(est && est.quota && est.usage / est.quota > 0.9);
}

export function requestStorageResetAndReload() {
  lsSet(FORCE_KEY, '1');
  window.location.reload();
}
